import { FC, useContext, useEffect, useId, useState, useSyncExternalStore } from 'react';
import { HiOutlineInformationCircle } from 'react-icons/hi2';
import { MdBugReport, MdClose } from 'react-icons/md';
import { FloatingPortal, flip, offset, shift, useFloating } from '@floating-ui/react';
import clsx from 'clsx';
import { canSeeDebugTools } from '@/utils/debug/canSeeDebugTools';
import { ReactReduxContext } from 'react-redux';
import { copyWithToast } from '../utils/debugPanelUtils';
import { ITableSchemeColumn, ITableSchemeDebug, ITableSchemeDetail } from './tableSchemeDebug';
import './TableSchemeDebugButton.scss';

interface ITableSchemeDebugButtonProps extends ITableSchemeDebug {
    columns: ITableSchemeColumn[];
    details?: ITableSchemeDetail[];
    /** Вложенные куски схемы таблицы: подтаблицы, наборы колонок, changes. */
    nested?: ITableSchemeDetail[];
    /** Класс иконки тулбара хоста (`dtb__icon-btn` или `atb__icon-btn`). */
    buttonClassName: string;
}

function isCount(value: string): boolean {
    return /^\d+$/.test(value);
}

/** Формула читается отдельным блоком, а не хвостом в правой колонке. */
function isFormula(value: string): boolean {
    return /[|?=]/.test(value);
}

/** Длинный список идентификаторов: имя сверху, элементы переносятся под ним. */
function isList(value: string): boolean {
    return value.includes(', ');
}

function AttrTree({ items }: { items: ITableSchemeDetail[] }) {
    return (
        <dl className="rf-table-scheme-debug__tree">
            {items.map((item, index) => {
                const children = item.children ?? [];
                const stacked = children.length > 0 || isFormula(item.value) || isList(item.value);
                const showValue = item.value.length > 0 && !(stacked && isCount(item.value));
                return (
                    <div key={`${item.label}-${index}`} className={stacked ? 'is-stack' : 'is-pair'}>
                        <div className="rf-table-scheme-debug__rowhead">
                            <dt>{item.label}</dt>
                            {item.hint ? <span className="rf-table-scheme-debug__hint">{item.hint}</span> : null}
                            {stacked && isCount(item.value) ? (
                                <span className="rf-table-scheme-debug__count">{item.value}</span>
                            ) : null}
                        </div>
                        {showValue ? (
                            <dd>
                                {isList(item.value) ? (
                                    <span className="rf-table-scheme-debug__tokens">
                                        {item.value.split(', ').map((token, tokenIndex) => (
                                            <span key={`${token}-${tokenIndex}`}>{token}</span>
                                        ))}
                                    </span>
                                ) : isFormula(item.value) ? (
                                    <span className="rf-table-scheme-debug__code">
                                        <PathText value={item.value} />
                                    </span>
                                ) : (
                                    item.value
                                )}
                            </dd>
                        ) : null}
                        {children.length > 0 ? (
                            <div className="rf-table-scheme-debug__nest">
                                <AttrTree items={children} />
                            </div>
                        ) : null}
                    </div>
                );
            })}
        </dl>
    );
}

function PathText({ value }: { value: string }) {
    const bits = value.split(/(\s+|==|!=|&&|\|\||[?:+*()/])/);
    return bits.map((bit, index) =>
        bit === '' ? null : /^(\s+|==|!=|&&|\|\||[?:+*()/])$/.test(bit) ? (
            <span key={index}>
                {bit}
                <wbr />
            </span>
        ) : (
            <span key={index}>{bit}</span>
        ),
    );
}

const ColumnAttrs: FC<{ column: ITableSchemeColumn }> = ({ column }) => {
    const [open, setOpen] = useState(false);
    const tipId = useId();
    const { refs, floatingStyles } = useFloating({
        open,
        strategy: 'fixed',
        placement: 'left-start',
        middleware: [offset(6), flip(), shift({ padding: 8 })],
    });

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return;
            event.stopImmediatePropagation();
            setOpen(false);
        };
        const onPointerDown = (event: MouseEvent) => {
            const target = event.target as Node;
            const reference = refs.reference.current;
            if (reference instanceof Node && reference.contains(target)) return;
            if (refs.floating.current?.contains(target)) return;
            setOpen(false);
        };
        document.addEventListener('keydown', onKey, true);
        document.addEventListener('mousedown', onPointerDown);
        return () => {
            document.removeEventListener('keydown', onKey, true);
            document.removeEventListener('mousedown', onPointerDown);
        };
    }, [open, refs]);

    if (!column.attributes?.length) return null;

    return (
        <>
            <button
                ref={(node) => {
                    refs.setReference(node);
                }}
                type="button"
                className={clsx('rf-table-scheme-debug__attr', open && 'is-open')}
                aria-label={`Атрибуты колонки ${column.header}`}
                aria-expanded={open}
                aria-describedby={open ? tipId : undefined}
                onClick={() => setOpen((value) => !value)}
            >
                <HiOutlineInformationCircle aria-hidden />
            </button>
            {open && (
                <FloatingPortal>
                    <div
                        ref={(node) => {
                            refs.setFloating(node);
                        }}
                        id={tipId}
                        role="tooltip"
                        className="rf-table-scheme-debug__tip"
                        style={floatingStyles}
                    >
                        <p className="rf-table-scheme-debug__tip-title">{column.header}</p>
                        <AttrTree items={column.attributes} />
                    </div>
                </FloatingPortal>
            )}
        </>
    );
};

/** Тот же допуск, что у панели отладки документа: все вне PROD и AdminPanel на PROD. */
function useCanSeeDebugPanel(): boolean {
    const redux = useContext(ReactReduxContext);
    const canViewAdminPanel = useSyncExternalStore(
        redux ? redux.store.subscribe : emptySubscribe,
        () => Boolean(redux?.store.getState()?.auth?.user?.canView?.adminPanel),
        () => false,
    );
    return canSeeDebugTools(canViewAdminPanel);
}

function emptySubscribe() {
    return () => {};
}

const TableSchemeDebugButton: FC<ITableSchemeDebugButtonProps> = ({
    title,
    tableKey,
    visibilityRules,
    sourcePath,
    columns,
    details,
    nested,
    buttonClassName,
}) => {
    const canDebug = useCanSeeDebugPanel();
    const [open, setOpen] = useState(false);
    const titleId = useId();
    const formula = visibilityRules?.trim() ?? '';
    const { refs, floatingStyles } = useFloating({
        open,
        strategy: 'fixed',
        placement: 'bottom-end',
        middleware: [offset(6), flip(), shift({ padding: 8 })],
    });

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setOpen(false);
        };
        const onPointerDown = (event: MouseEvent) => {
            const target = event.target as Node;
            const reference = refs.reference.current;
            if (reference instanceof Node && reference.contains(target)) return;
            if (refs.floating.current?.contains(target)) return;
            if (target instanceof Element && target.closest('.rf-table-scheme-debug__tip')) return;
            setOpen(false);
        };
        document.addEventListener('keydown', onKey);
        document.addEventListener('mousedown', onPointerDown);
        return () => {
            document.removeEventListener('keydown', onKey);
            document.removeEventListener('mousedown', onPointerDown);
        };
    }, [open, refs]);

    if (!canDebug) return null;

    return (
        <>
            <button
                ref={(node) => {
                    refs.setReference(node);
                }}
                type="button"
                className={clsx(buttonClassName, open && 'is-active')}
                aria-label="Схема таблицы"
                aria-expanded={open}
                title="Схема таблицы"
                onClick={() => setOpen((value) => !value)}
            >
                <MdBugReport aria-hidden />
            </button>
            {open && (
                <FloatingPortal>
                    <div
                        ref={(node) => {
                            refs.setFloating(node);
                        }}
                        className="rf-table-scheme-debug"
                        style={floatingStyles}
                        role="dialog"
                        aria-labelledby={titleId}
                    >
                        <div className="rf-table-scheme-debug__head">
                            <div>
                                <h2 id={titleId} className="rf-table-scheme-debug__title">
                                    {title || tableKey}
                                </h2>
                                <p className="rf-table-scheme-debug__key">{tableKey}</p>
                            </div>
                            <button
                                type="button"
                                className="rf-table-scheme-debug__close"
                                aria-label="Закрыть"
                                onClick={() => setOpen(false)}
                            >
                                <MdClose aria-hidden />
                            </button>
                        </div>
                        <p className="rf-table-scheme-debug__source">
                            {sourcePath.startsWith('/') ? `GET ${sourcePath}` : sourcePath}
                        </p>
                        <div className="rf-table-scheme-debug__body">
                            {visibilityRules !== undefined && (
                                <section className="rf-table-scheme-debug__block">
                                    <div className="rf-table-scheme-debug__rowline">
                                        <h3>Видимость</h3>
                                        {formula && (
                                            <button
                                                type="button"
                                                className="rf-table-scheme-debug__copy"
                                                onClick={() => copyWithToast(formula)}
                                            >
                                                Копировать
                                            </button>
                                        )}
                                    </div>
                                    <pre className="rf-table-scheme-debug__formula">{formula || 'не задана'}</pre>
                                </section>
                            )}
                            <section className="rf-table-scheme-debug__block">
                                <h3>Колонки · {columns.length}</h3>
                                {columns.length > 0 ? (
                                    <ul className="rf-table-scheme-debug__cols">
                                        {columns.map((column) => (
                                            <li key={column.field}>
                                                <div className="rf-table-scheme-debug__col-name">
                                                    <span className="rf-table-scheme-debug__col-title">
                                                        <span>{column.header}</span>
                                                        <ColumnAttrs column={column} />
                                                    </span>
                                                    {column.dataType && (
                                                        <span className="rf-table-scheme-debug__type">{column.dataType}</span>
                                                    )}
                                                </div>
                                                <div className="rf-table-scheme-debug__field">
                                                    <PathText value={column.field} />
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="rf-table-scheme-debug__empty">Колонки схемы ещё не загрузились.</p>
                                )}
                            </section>
                            {nested && nested.length > 0 && (
                                <section className="rf-table-scheme-debug__block">
                                    <h3>Вложенное</h3>
                                    <AttrTree items={nested} />
                                </section>
                            )}
                        </div>
                        {details && details.length > 0 && (
                            <p className="rf-table-scheme-debug__meta">
                                {details.map((detail) => (
                                    <span key={detail.label}>
                                        {detail.label} <b>{detail.value}</b>
                                    </span>
                                ))}
                            </p>
                        )}
                    </div>
                </FloatingPortal>
            )}
        </>
    );
};

export default TableSchemeDebugButton;
